import { CURRENCY_SYMBOLS_BY_CODE } from "../constants";

/**
 * Валюты, у которых символ идёт после суммы (референс: shared/utils/currency.js,
 * паттерн "#!"/"# !" у zł, ₽, ₸ и т.п.). MDL в референс не входит, добавлена
 * по той же схеме: "1 000 L", символ через пробел после числа. PLN так же:
 * "1 000 zł".
 */
const SUFFIX_CURRENCIES = new Set(["MDL", "PLN"]);

/**
 * Форматирование денежной суммы (референс: shared/utils/currency.js,
 * без XTR, у нас EUR/USD/MDL/GBP/CAD/PLN; GBP и CAD символом перед
 * суммой, "£1 000" и "CA$1 000"). currency принимает string: PaymentCurrency
 * (валюта организации/платежа) и ProductCurrency (валюта цены продукта)
 * совпадают по набору значений, но это разные enum'ы схемы.
 *
 * Живёт в shared/utils (не в features/products), потому что её использует
 * и shared/components/BarChart: boundaries/element-types запрещает shared
 * импортировать напрямую из features (кроме подпапки feature-ui), поэтому
 * features/products/utils/formatCurrency.ts переэкспортирует её отсюда,
 * чтобы не менять существующие импорты.
 */
export function formatCurrency(value: number, currency: string): string {
  const symbol = CURRENCY_SYMBOLS_BY_CODE[currency] ?? currency;
  const precision = value % 1 === 0 ? 0 : 2;
  const [integerPart, decimalPart] = value.toFixed(precision).split(".");
  // референс: shared/utils/currency.js, separator: " " (разряды тысяч
  // разделены пробелом, "$1 000", а не "$1000"/"$1,000").
  const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const amount = `${groupedInteger}${decimalPart ? `.${decimalPart}` : ""}`;
  return SUFFIX_CURRENCIES.has(currency)
    ? `${amount} ${symbol}`
    : `${symbol}${amount}`;
}

/**
 * Форматирование объекта Amount { value, currency } из партнёрской схемы
 * (Payment.amount, organizationAmountInOrganizationCurrency и т.п.):
 * amount может быть null (например organizationAmountInOrganizationCurrency
 * до расчёта комиссий), тогда возвращается fallback.
 */
export function formatAmountObject(
  amount: { value: number; currency: string } | null | undefined,
  fallback = ""
): string {
  return amount ? formatCurrency(amount.value, amount.currency) : fallback;
}
